import { Box } from "@mui/material";
import { type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { type FlatSwitchOption, SystemFlatSwitch } from "../../components/UIToolkit/Clickable/SystemFlatSwitch";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { localStorageService } from "../../services/LocalStorageService";
import { ActivityLog } from "./ActivityLog";
import { GroupedActivityLog } from "./GroupedActivityLog/GroupedActivityLog";
import { hasGroupedActivityLogUrlParams, stripGroupedActivityLogUrlParams } from "./GroupedActivityLog/groupedActivityLogUrlState";

type ActivityLogView = "grouped" | "raw";

/**
 * Route-level wrapper for `/activity-log`: owns the Grouped/Raw switch and renders exactly one of the
 * two views below it. Neither view carries its own page title duplicate — `ActivityLog.tsx` already
 * has one, and `GroupedActivityLog.tsx` renders a matching one — so both modes read the same
 * switch → title → filters → grid order.
 */
export const ActivityLogPage: FC = () => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const { chosenDepartment } = useHospitalData();
	const [storedView, setStoredView] = useState<ActivityLogView>(localStorageService.getActivityLogViewMode);
	const [searchParams, setSearchParams] = useSearchParams();
	// A link carrying Grouped filters opens Grouped whatever this user last picked — otherwise a shared
	// filtered link would land a Raw-view user on a page ignoring every filter in it. The stored choice
	// is left alone: following someone's link is not the user switching views.
	const forcedGrouped = hasGroupedActivityLogUrlParams(searchParams);
	const view: ActivityLogView = forcedGrouped ? "grouped" : storedView;

	const options: FlatSwitchOption<ActivityLogView>[] = [
		{ label: t("activityLog.switch.grouped"), value: "grouped" },
		{ label: t("activityLog.switch.raw"), value: "raw" },
	];

	const handleSwitch = (option: FlatSwitchOption<ActivityLogView>) => {
		setStoredView(option.value);
		localStorageService.setActivityLogViewMode(option.value);
		// Raw has no URL filters. Dropping Grouped's (replacing the history entry) is what keeps a reload
		// on Raw instead of the leftover parameters forcing Grouped again.
		if (option.value === "raw") setSearchParams((current) => stripGroupedActivityLogUrlParams(current), { replace: true });
		track("activity_log_view_switched", { department_id: chosenDepartment?.department_id, view: option.value });
	};

	const switcherToggle = <SystemFlatSwitch options={options} activeIndexOrValue={view} onSwitch={handleSwitch} data-testid="activity-log-view-switch" />;

	return (
		<Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", minHeight: 0, minWidth: 0 }} data-testid="activity-log-page">
			<Box sx={{ flex: 1, minHeight: 0 }}>
				{view === "grouped" ? (
					// Keyed by department (2026-09-08 fix): GroupedActivityLog's default date range/filters are
					// derived once at mount (useState lazy initializers, including the department-timezoned
					// actionTimeStart/actionTimeEnd) — switching departments changes `chosenDepartment`/`timezone`
					// via context without remounting this component, so an untouched default silently stayed
					// zoned to the PREVIOUS department. A remount (via `key`) re-derives every default fresh
					// against the new department, rather than selectively resyncing just the date range's zone
					// and risking a mismatch with a performedBy/channel/etc. filter still scoped to the old one.
					<GroupedActivityLog key={chosenDepartment?.department_id} titleTrailingNode={switcherToggle} />
				) : (
					<ActivityLog titleTrailingNode={switcherToggle} />
				)}
			</Box>
		</Box>
	);
};
